import { useState, useEffect } from "react";
import { signInWithGoogle, logOut, onAuthParamChange, loginWithEmail } from "./firebase";

// --- CONSTANTS ---
const CLAUDE_MODELS = [
  { id:"claude-sonnet-4-5-20250929", label:"Claude Sonnet 4.5", badge:"Default · Best Value",  cost:"$3/$15 per 1M" },
  { id:"claude-opus-4-6",            label:"Claude Opus 4.6",   badge:"Latest · Best Quality", cost:"$5/$25 per 1M" },
  { id:"claude-opus-4-5-20251101",   label:"Claude Opus 4.5",   badge:"Previous Opus",         cost:"$15/$75 per 1M" },
  { id:"claude-haiku-4-5-20251001",  label:"Claude Haiku 4.5",  badge:"Fastest · Cheapest",    cost:"$0.80/$4 per 1M" },
];

const VIDEO_APIS = [
  { id:"kling",    label:"Kling AI",       badge:"Recommended",          status:"live",    cost:"~$0.14/sec",       keyPlaceholder:"kl-...",   keyHint:"klingai.com → Developer → API Keys" },
  { id:"heygen",   label:"HeyGen",         badge:"Best for Talking Head", status:"live",    cost:"~$0.30/avatar min", keyPlaceholder:"hg-...",   keyHint:"app.heygen.com → Settings → API" },
  { id:"runway",   label:"Runway Gen-4.5", badge:"Highest Quality",      status:"live",    cost:"~$0.25/sec",       keyPlaceholder:"key_...",  keyHint:"app.runwayml.com → Account → API Keys" },
  { id:"seedance", label:"Seedance 2.0",   badge:"API ~Feb 24",          status:"pending", cost:"~$0.10/sec (est.)", keyPlaceholder:"sd-...",   keyHint:"console.volcengine.com → API Keys" },
];

const VERTICAL_GROUPS = [
  { id:"home",      label:"🏠 Home Services", color:"#7c3aed", services:["Roofing","Bathroom Remodeling","Kitchen Renovation","Water Heater Installation","Gutter Services","Windows & Doors","HVAC","Solar Panels","Flooring","Interior Painting","Fencing","Siding","Landscaping","Garage Doors","General Home Improvement"] },
  { id:"insurance", label:"🛡️ Insurance",    color:"#0369a1", services:["Auto Insurance","Home Insurance","Life Insurance","Health Insurance","Renters Insurance","Business Insurance"] },
];

const AD_TYPES = [
  { id:"talking-head", label:"Talking Head",       icon:"🎙️", desc:"Spokesperson UGC-style pitch" },
  { id:"broll",        label:"B-Roll / Lifestyle",  icon:"🏡", desc:"Service in use, lifestyle scenes" },
  { id:"product-demo", label:"Product Demo",        icon:"🔧", desc:"Before/after, how it works" },
  { id:"multishot",    label:"Multi-Shot",           icon:"🎬", desc:"Narrative across multiple scenes" },
];

const PLATFORMS = [
  { id:"tiktok",   label:"TikTok",   ratio:"9:16", icon:"♪", maxSec:30 },
  { id:"facebook", label:"Facebook", ratio:"1:1",  icon:"f", maxSec:30 },
  { id:"youtube",  label:"YouTube",  ratio:"16:9", icon:"▶", maxSec:60 },
];

const HOOKS = {
  "roofing":             ["POV: your roof just leaked and now you're scrambling...", "I ignored this for 3 years and it cost me $12,000", "The #1 sign your roof needs replacing TODAY"],
  "bathroom remodeling": ["This $8,000 bathroom took 3 days. Here's how.", "POV: you finally got the bathroom you deserved", "We turned this disaster into a dream bathroom"],
  "kitchen renovation":  ["We spent $15k on our kitchen and got $40k back at resale", "POV: you finally have the kitchen you cook in, not around", "Our kitchen was embarrassing — here's the 3-day fix"],
  "flooring":            ["We ripped out carpet that was 22 years old. Look what was underneath.", "POV: you finally got rid of the flooring you hated for years", "This floor transformation took one weekend"],
  "interior painting":   ["POV: one coat of paint completely changed how this room feels", "I spent $400 on paint and my house looks like a different home", "The color everyone keeps asking about"],
  "fencing":             ["Our neighbors kept looking into our backyard until we did this", "POV: you finally got the privacy you wanted", "This fence went up in one day"],
  "siding":              ["Our house looked 30 years older than it was until last month", "POV: new siding and suddenly your home is the nicest on the block", "We ignored the siding for too long"],
  "auto insurance":      ["I was overpaying $1,400 a year and did not know it", "POV: you switched insurers in 4 minutes and saved $900", "The trick insurance companies do not advertise"],
  "home insurance":      ["Our neighbor's claim got denied. Here's what we did differently.", "POV: a tree fell on your house and your policy does not cover it", "Most homeowners have the wrong coverage"],
  "life insurance":      ["At 34, I finally got life insurance. Here's what I wish I knew at 25.", "POV: you realize your family would get $0 if something happened to you today", "The cheapest time to buy is right now"],
  "health insurance":    ["The open enrollment mistake that cost my friend $8,000", "POV: you finally found a plan that actually covers what you need", "I compared 12 health plans so you do not have to"],
  "default":             ["I wish someone told me this sooner...", "POV: you finally stopped procrastinating on this", "Here is what the pros do not want you to know"],
};

const getHooks = (v) => HOOKS[v.toLowerCase()] || HOOKS.default;
const getGroup = (v) => VERTICAL_GROUPS.find(g => g.services.includes(v));

const S = {
  input: { width:"100%", background:"#0a0a18", border:"1px solid #1e1e3a", borderRadius:"8px", padding:"10px 14px", color:"#c4c4e0", fontSize:"13px", outline:"none", boxSizing:"border-box", fontFamily:"'DM Sans',sans-serif" },
  label: { fontSize:"11px", fontWeight:700, letterSpacing:"0.1em", color:"#6d6d9e", textTransform:"uppercase", marginBottom:"7px", display:"block", fontFamily:"'Space Mono',monospace" },
  btn:   { background:"none", border:"1px solid #312e81", color:"#7c7caa", borderRadius:"6px", padding:"5px 12px", fontSize:"11px", cursor:"pointer", fontFamily:"'Space Mono',monospace", marginTop:"10px" },
};

// --- API HELPER ---
async function generateAdScript({ vertical, adType, platform, hook, audience, offer, tone, model, claudeKey }) {
  const pd = PLATFORMS.find(p => p.id === platform);
  const ad = AD_TYPES.find(a => a.id === adType);
  const grp = getGroup(vertical);
  const ind = grp?.id === "insurance" ? "insurance lead generation" : "home services lead generation";
  const sys = `Expert performance marketing copywriter for ${ind}. Write UGC-style scripts for ${pd?.label}. Rules: hook in first 3s, real person voice, specific numbers, clear CTA. Format: ${pd?.ratio}, max ${pd?.maxSec}s. Return ONLY valid JSON, no markdown: {"hook":"...","script":"...","shotList":["..."],"videoPrompt":"...","cta":"...","estimatedDuration":0,"onScreenText":["..."]}`;
  const usr = `${ad?.label} UGC ad. Service: ${vertical}. Platform: ${pd?.label} (${pd?.ratio}, max ${pd?.maxSec}s). Hook: "${hook}". Audience: ${audience||(grp?.id==="insurance"?"adults 25-55 shopping for better rates":"homeowners 35-65")}. Offer: ${offer||(grp?.id==="insurance"?"Free quote in 2 minutes":"Free estimate")}. Tone: ${tone||"authentic, urgent, conversational"}. Style: ${ad?.desc}. Make it feel like real UGC.`;
  const headers = { "Content-Type": "application/json" };
  if (claudeKey?.trim()) headers["x-api-key"] = claudeKey.trim();
  const res = await fetch("https://api.anthropic.com/v1/messages", { method:"POST", headers, body:JSON.stringify({ model, max_tokens:1000, system:sys, messages:[{role:"user",content:usr}] }) });
  const data = await res.json();
  if (data.error) return { error: data.error.message || "API error" };
  const raw = data.content?.map(b => b.text||"").join("")||"";
  try { return JSON.parse(raw.replace(/```json|```/g,"").trim()); }
  catch { return { error:"JSON parse error, try again", raw }; }
}

// --- SUB-COMPONENTS ---
function Badge({ text, variant="default" }) {
  const V = { default:{background:"#1a1a2e",color:"#a78bfa",border:"1px solid #312e81"}, live:{background:"#052e16",color:"#4ade80",border:"1px solid #166534"}, pending:{background:"#1c1917",color:"#fb923c",border:"1px solid #9a3412"}, rec:{background:"#0f172a",color:"#38bdf8",border:"1px solid #0369a1"} };
  return <span style={{...V[variant],fontSize:"10px",fontWeight:700,letterSpacing:"0.08em",padding:"2px 8px",borderRadius:"4px",textTransform:"uppercase",fontFamily:"'Space Mono',monospace",whiteSpace:"nowrap"}}>{text}</span>;
}

function Card({ title, children, accent, color, style={} }) {
  return (
    <div style={{background:accent?"linear-gradient(135deg,#0f0c29,#1a1040)":"#0d0d1a",border:`1px solid ${color||(accent?"#5b21b6":"#1e1e3a")}`,borderRadius:"12px",padding:"20px",marginBottom:"16px",...style}}>
      {title && <div style={{fontSize:"11px",fontWeight:700,letterSpacing:"0.12em",color:color||"#6d6d9e",textTransform:"uppercase",marginBottom:"14px",fontFamily:"'Space Mono',monospace"}}>{title}</div>}
      {children}
    </div>
  );
}

function EyeInput({ value, onChange, placeholder }) {
  const [vis, setVis] = useState(false);
  return (
    <div style={{display:"flex",gap:"8px"}}>
      <input type={vis?"text":"password"} value={value} onChange={onChange} placeholder={placeholder} style={{...S.input,flex:1}} />
      <button onClick={()=>setVis(!vis)} style={{background:"#111128",border:"1px solid #1e1e3a",borderRadius:"8px",padding:"0 12px",color:"#6d6d9e",cursor:"pointer",fontSize:"14px",flexShrink:0}}>{vis?"🙈":"👁"}</button>
    </div>
  );
}

// --- SETTINGS MODAL ---
function SettingsModal({ open, onClose, claudeModel, setClaudeModel, claudeKey, setClaudeKey, videoKeys, setVideoKeys }) {
  const [tab, setTab] = useState("claude");
  if (!open) return null;

  const setVKey = (id, val) => setVideoKeys(prev => ({ ...prev, [id]: val }));
  const activeApi = VIDEO_APIS.find(a => a.id === tab);
  const keyCount = (claudeKey ? 1 : 0) + Object.values(videoKeys).filter(v => v?.trim()).length;

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.8)",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:"16px"}} onClick={onClose}>
      <div style={{background:"#0d0d1a",border:"1px solid #312e81",borderRadius:"16px",width:"100%",maxWidth:"560px",maxHeight:"90vh",overflow:"hidden",display:"flex",flexDirection:"column"}} onClick={e=>e.stopPropagation()}>

        {/* Modal Header */}
        <div style={{padding:"20px 24px 0",display:"flex",justifyContent:"space-between",alignItems:"center",flexShrink:0}}>
          <div style={{fontWeight:700,fontSize:"15px",color:"#e2e8f0"}}>⚙️ API Settings</div>
          <button onClick={onClose} style={{background:"none",border:"none",color:"#6d6d9e",cursor:"pointer",fontSize:"20px",lineHeight:1}}>✕</button>
        </div>

        {/* Tab Bar */}
        <div style={{display:"flex",gap:"0",padding:"16px 24px 0",flexShrink:0,borderBottom:"1px solid #1e1e3a",overflowX:"auto"}}>
          {[
            { id:"claude", label:"🤖 Claude" + (claudeKey ? "  ✓" : "") },
            ...VIDEO_APIS.map(a => ({ id:a.id, label:a.label + (videoKeys[a.id] ? "  ✓" : "") }))
          ].map(t => (
            <button key={t.id} onClick={()=>setTab(t.id)} style={{
              background:"none", border:"none", borderBottom:`2px solid ${tab===t.id?"#7c3aed":"transparent"}`,
              padding:"8px 14px", cursor:"pointer", color:tab===t.id?"#e2e8f0":"#6d6d9e",
              fontSize:"12px", fontWeight:600, whiteSpace:"nowrap", fontFamily:"'DM Sans',sans-serif"
            }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div style={{padding:"24px",overflowY:"auto",flex:1}}>

          {/* Claude Tab */}
          {tab === "claude" && (
            <div>
              <label style={S.label}>Claude Model</label>
              <div style={{display:"flex",flexDirection:"column",gap:"8px",marginBottom:"20px"}}>
                {CLAUDE_MODELS.map(m => (
                  <button key={m.id} onClick={()=>setClaudeModel(m.id)} style={{
                    display:"flex", alignItems:"center", justifyContent:"space-between",
                    background:claudeModel===m.id?"linear-gradient(135deg,#1a0a3a,#0f0c29)":"#111128",
                    border:`1px solid ${claudeModel===m.id?"#7c3aed":"#1e1e3a"}`,
                    borderRadius:"8px", padding:"11px 14px", cursor:"pointer", fontFamily:"inherit"
                  }}>
                    <div style={{textAlign:"left"}}>
                      <div style={{fontSize:"13px",fontWeight:600,color:claudeModel===m.id?"#e2e8f0":"#9090b8"}}>{m.label}</div>
                      <div style={{fontSize:"11px",color:"#4a4a6a",marginTop:"2px",fontFamily:"'Space Mono',monospace"}}>{m.cost}</div>
                    </div>
                    <Badge text={m.badge} variant={claudeModel===m.id?"live":"default"} />
                  </button>
                ))}
              </div>

              <label style={S.label}>Claude API Key</label>
              <EyeInput value={claudeKey} onChange={e=>setClaudeKey(e.target.value)} placeholder="sk-ant-api03-..." />
              <div style={{fontSize:"11px",color:"#2a2a4a",fontFamily:"'Space Mono',monospace",marginTop:"8px",lineHeight:1.7}}>
                🔒 Stored in memory only · never saved to disk · only sent to api.anthropic.com<br/>
                📌 Get from console.anthropic.com → API Keys
              </div>
              {claudeKey && <div style={{background:"#052e16",border:"1px solid #166534",borderRadius:"8px",padding:"10px 14px",marginTop:"12px",fontSize:"12px",color:"#4ade80"}}>✓ Custom API key active — all generations use your key</div>}
            </div>
          )}

          {/* Video API Tabs */}
          {activeApi && tab !== "claude" && (
            <div>
              <div style={{display:"flex",alignItems:"center",gap:"10px",marginBottom:"18px"}}>
                <Badge text={activeApi.badge} variant={activeApi.status==="live"?"live":"pending"} />
                <span style={{fontSize:"12px",color:"#6d6d9e",fontFamily:"'Space Mono',monospace"}}>{activeApi.cost}</span>
              </div>

              {activeApi.status === "pending" && (
                <div style={{background:"#1c0a00",border:"1px solid #9a3412",borderRadius:"8px",padding:"12px 14px",marginBottom:"16px",fontSize:"12px",color:"#fb923c",lineHeight:1.6}}>
                  ⚠️ {activeApi.label} API not yet launched. Expected ~Feb 24, 2026.<br/>
                  You can save your key here now — it will activate automatically once live.
                </div>
              )}

              <label style={S.label}>{activeApi.label} API Key</label>
              <EyeInput value={videoKeys[activeApi.id]||""} onChange={e=>setVKey(activeApi.id,e.target.value)} placeholder={activeApi.keyPlaceholder} />
              <div style={{fontSize:"11px",color:"#2a2a4a",fontFamily:"'Space Mono',monospace",marginTop:"8px",lineHeight:1.7}}>
                📌 {activeApi.keyHint}
              </div>
              {videoKeys[activeApi.id] && (
                <div style={{background:"#052e16",border:"1px solid #166534",borderRadius:"8px",padding:"10px 14px",marginTop:"12px",fontSize:"12px",color:"#4ade80"}}>
                  ✓ {activeApi.label} key saved
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div style={{padding:"16px 24px",borderTop:"1px solid #1e1e3a",display:"flex",justifyContent:"space-between",alignItems:"center",flexShrink:0}}>
          <div style={{fontSize:"12px",color:"#6d6d9e"}}>🔑 {keyCount} key{keyCount!==1?"s":""} configured</div>
          <button onClick={onClose} style={{background:"linear-gradient(135deg,#7c3aed,#3b82f6)",border:"none",padding:"8px 20px",borderRadius:"6px",color:"#fff",fontSize:"12px",fontWeight:700,cursor:"pointer"}}>Done</button>
        </div>
      </div>
    </div>
  );
}

// --- LOGIN COMPONENT (AuthGate) ---
function AuthGate() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await loginWithEmail(email, password);
    } catch (err) {
      console.error(err);
      setError("Login failed: " + err.message);
    }
  };

  return (
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"radial-gradient(ellipse at top,#0d0726 0%,#050510 60%)",fontFamily:"'DM Sans',sans-serif"}}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;600;700&family=Space+Mono:wght@400;700&display=swap');*{box-sizing:border-box}`}</style>
      <div style={{background:"#0d0d1a",border:"1px solid #1e1e3a",borderRadius:"16px",padding:"40px",width:"100%",maxWidth:"380px",textAlign:"center"}}>
        <div style={{fontSize:"36px",marginBottom:"10px"}}>🎬</div>
        <div style={{fontSize:"20px",fontWeight:700,color:"#e2e8f0",marginBottom:"6px"}}>UGC Ad Generator</div>
        <div style={{fontSize:"11px",color:"#4a4a6a",marginBottom:"20px",fontFamily:"'Space Mono',monospace",letterSpacing:"0.08em"}}>INTERNAL TEAM ACCESS</div>

        {/* GOOGLE BUTTON */}
        <button 
          onClick={signInWithGoogle} 
          style={{width:"100%",padding:"13px",border:"none",borderRadius:"10px",cursor:"pointer",background:"linear-gradient(135deg,#7c3aed,#3b82f6)",color:"#fff",fontSize:"14px",fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",gap:"10px",marginBottom:"20px",fontFamily:"inherit"}}
        >
          <svg width="18" height="18" viewBox="0 0 48 48" style={{flexShrink:0}}>
            <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
            <path fill="#FF3D00" d="m6.306 14.691 6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
            <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
            <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
          </svg>
          Sign in with Google
        </button>
        
        <div style={{display:"flex",alignItems:"center",gap:"10px",color:"#4a4a6a",fontSize:"12px",marginBottom:"20px"}}>
          <div style={{height:"1px",background:"#1e1e3a",flex:1}}></div>OR<div style={{height:"1px",background:"#1e1e3a",flex:1}}></div>
        </div>

        {/* EMAIL FORM */}
        <form onSubmit={handleEmailLogin} style={{display:"flex",flexDirection:"column",gap:"10px"}}>
          <input 
            type="email" 
            placeholder="Email" 
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={{background:"#0a0a18",border:"1px solid #1e1e3a",borderRadius:"8px",padding:"12px",color:"white",outline:"none",fontFamily:"inherit"}}
            required
          />
          <input 
            type="password" 
            placeholder="Password" 
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={{background:"#0a0a18",border:"1px solid #1e1e3a",borderRadius:"8px",padding:"12px",color:"white",outline:"none",fontFamily:"inherit"}}
            required
          />
          <button type="submit" style={{background:"none",border:"1px solid #312e81",color:"#a78bfa",padding:"10px",borderRadius:"8px",cursor:"pointer",fontSize:"13px",fontWeight:700,fontFamily:"inherit"}}>
            Sign in with Email
          </button>
        </form>

        {error && <div style={{color:"#ef4444",fontSize:"12px",marginTop:"15px"}}>{error}</div>}
      </div>
    </div>
  );
}

// --- MAIN APP COMPONENT (The Ad Generator) ---
function AuthenticatedApp({ user }) {
  // 1. STATE MANAGEMENT
  const [vertical, setVertical] = useState(VERTICAL_GROUPS[0].services[0]);
  const [adType, setAdType] = useState(AD_TYPES[0].id);
  const [platform, setPlatform] = useState(PLATFORMS[0].id);
  const [hook, setHook] = useState(HOOKS["roofing"][0]);
  const [customHook, setCustomHook] = useState("");
  const [audience, setAudience] = useState("");
  const [offer, setOffer] = useState("");
  const [tone, setTone] = useState("");
  const [claudeModel, setClaudeModel] = useState(CLAUDE_MODELS[0].id);
  const [claudeKey, setClaudeKey] = useState("");
  const [videoKeys, setVideoKeys] = useState({ kling:"", heygen:"", runway:"", seedance:"" });
  const [settingsOpen, setSettingsOpen] = useState(false);
  
  const [generated, setGenerated] = useState(null);
  const [loading, setLoading] = useState(false);

  // Count configured keys for badge
  const keyCount = (claudeKey ? 1 : 0) + Object.values(videoKeys).filter(v => v?.trim()).length;

  // Update hooks when vertical changes
  useEffect(() => {
    const newHooks = getHooks(vertical);
    setHook(newHooks[0]);
  }, [vertical]);

  // 2. GENERATION HANDLER
  const handleGenerate = async () => {
    if (!claudeKey?.trim()) {
      setGenerated({ error: "Please add your Claude API key in ⚙️ Settings first." });
      return;
    }
    setLoading(true);
    setGenerated(null);
    const result = await generateAdScript({
      vertical,
      adType,
      platform,
      hook: customHook || hook,
      audience,
      offer,
      tone,
      model: claudeModel,
      claudeKey
    });
    setGenerated(result);
    setLoading(false);
  };

  // 3. UI RENDER
  return (
    <div style={{minHeight:"100vh", background:"#050510", color:"#c4c4e0", fontFamily:"'DM Sans',sans-serif", paddingBottom:"40px"}}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;600;700&family=Space+Mono:wght@400;700&display=swap');*{box-sizing:border-box}`}</style>
      
      {/* SETTINGS MODAL */}
      <SettingsModal
        open={settingsOpen}
        onClose={()=>setSettingsOpen(false)}
        claudeModel={claudeModel}
        setClaudeModel={setClaudeModel}
        claudeKey={claudeKey}
        setClaudeKey={setClaudeKey}
        videoKeys={videoKeys}
        setVideoKeys={setVideoKeys}
      />

      {/* HEADER */}
      <div style={{borderBottom:"1px solid #1e1e3a", background:"#0d0d1a", padding:"15px 30px", marginBottom:"30px", display:"flex", justifyContent:"space-between", alignItems:"center"}}>
        <div style={{display:"flex", alignItems:"center", gap:"12px"}}>
          <div style={{fontSize:"20px"}}>🎬</div>
          <div>
            <div style={{fontWeight:700, color:"#fff", fontSize:"14px"}}>UGC Ad Generator</div>
            <div style={{fontSize:"11px", color:"#6d6d9e", fontFamily:"'Space Mono',monospace"}}>INTERNAL TOOL · V2.0</div>
          </div>
        </div>
        <div style={{display:"flex", alignItems:"center", gap:"12px"}}>
          {/* KEY COUNT BADGE */}
          <div style={{fontSize:"12px", color:keyCount>0?"#4ade80":"#6d6d9e", fontFamily:"'Space Mono',monospace"}}>
            🔑 {keyCount} key{keyCount!==1?"s":""} set
          </div>
          {/* SETTINGS BUTTON */}
          <button 
            onClick={()=>setSettingsOpen(true)} 
            style={{
              background:"#1a1a2e", border:"1px solid #312e81", color:"#a78bfa", 
              padding:"6px 14px", borderRadius:"6px", cursor:"pointer", fontSize:"13px", 
              fontWeight:700, display:"flex", alignItems:"center", gap:"6px",
              transition:"all 0.2s"
            }}
            onMouseEnter={e=>{e.target.style.background="#252550";e.target.style.borderColor="#7c3aed"}}
            onMouseLeave={e=>{e.target.style.background="#1a1a2e";e.target.style.borderColor="#312e81"}}
          >
            ⚙️ Settings
          </button>
          {/* USER INFO */}
          <div style={{fontSize:"12px", color:"#6d6d9e"}}>
            <span style={{color:"#a78bfa"}}>{user.email}</span>
          </div>
          <button onClick={logOut} style={{background:"#1a1a2e", border:"1px solid #312e81", color:"#a78bfa", padding:"6px 16px", borderRadius:"6px", cursor:"pointer", fontSize:"11px", fontWeight:700}}>SIGN OUT</button>
        </div>
      </div>

      <div style={{maxWidth:"1100px", margin:"0 auto", padding:"0 20px", display:"grid", gridTemplateColumns:"360px 1fr", gap:"30px"}}>
        
        {/* LEFT COLUMN: CONTROLS */}
        <div>
          <Card title="Configuration" accent>
            {/* VERTICAL SELECTOR */}
            <label style={S.label}>Vertical / Niche</label>
            <select value={vertical} onChange={(e)=>setVertical(e.target.value)} style={{...S.input, marginBottom:"16px"}}>
              {VERTICAL_GROUPS.map(g => (
                <optgroup key={g.id} label={g.label}>
                  {g.services.map(s => <option key={s} value={s}>{s}</option>)}
                </optgroup>
              ))}
            </select>

            {/* AD TYPE */}
            <label style={S.label}>Ad Format</label>
            <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:"8px", marginBottom:"16px"}}>
              {AD_TYPES.map(t => (
                <div key={t.id} onClick={()=>setAdType(t.id)} 
                  style={{border:`1px solid ${adType===t.id?"#7c3aed":"#1e1e3a"}`, background:adType===t.id?"#1e1b4b":"#0a0a18", padding:"10px", borderRadius:"8px", cursor:"pointer", textAlign:"center", transition:"all 0.2s"}}>
                  <div style={{fontSize:"18px", marginBottom:"4px"}}>{t.icon}</div>
                  <div style={{fontSize:"10px", fontWeight:700, color:adType===t.id?"#fff":"#6d6d9e"}}>{t.label}</div>
                </div>
              ))}
            </div>

            {/* PLATFORM */}
            <label style={S.label}>Platform</label>
            <div style={{display:"flex", gap:"8px", marginBottom:"16px"}}>
              {PLATFORMS.map(p => (
                <button key={p.id} onClick={()=>setPlatform(p.id)} style={{flex:1, padding:"8px", border:`1px solid ${platform===p.id?"#3b82f6":"#1e1e3a"}`, background:platform===p.id?"#172554":"#0a0a18", color:platform===p.id?"#fff":"#6d6d9e", borderRadius:"6px", cursor:"pointer", fontSize:"12px", fontWeight:700}}>
                   {p.icon} {p.label}
                </button>
              ))}
            </div>
          </Card>

          <Card title="Script Details">
            {/* HOOK */}
            <label style={S.label}>Hook (First 3 Seconds)</label>
            <select value={hook} onChange={(e)=>{setHook(e.target.value); setCustomHook("")}} style={{...S.input, marginBottom:"8px"}}>
               {getHooks(vertical).map((h,i) => <option key={i} value={h}>{h.substring(0,40)}...</option>)}
            </select>
            <input type="text" placeholder="Or write custom hook..." value={customHook} onChange={(e)=>setCustomHook(e.target.value)} style={{...S.input, marginBottom:"16px", borderColor:customHook?"#a78bfa":"#1e1e3a"}} />

            {/* AUDIENCE & OFFER */}
            <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px", marginBottom:"16px"}}>
              <div>
                <label style={S.label}>Target Audience</label>
                <input placeholder="e.g. Homeowners 40+" value={audience} onChange={(e)=>setAudience(e.target.value)} style={S.input} />
              </div>
              <div>
                <label style={S.label}>Main Offer</label>
                <input placeholder="e.g. Free Quote" value={offer} onChange={(e)=>setOffer(e.target.value)} style={S.input} />
              </div>
            </div>

            {/* NO API KEY REMINDER */}
            {!claudeKey && (
              <div 
                onClick={()=>setSettingsOpen(true)}
                style={{background:"#1c1917",border:"1px solid #9a3412",borderRadius:"8px",padding:"12px 14px",marginBottom:"16px",fontSize:"12px",color:"#fb923c",cursor:"pointer",lineHeight:1.6}}
              >
                ⚠️ No Claude API key set — click here or use ⚙️ Settings to add one before generating.
              </div>
            )}
            
            <button onClick={handleGenerate} disabled={loading} style={{width:"100%", marginTop:"4px", background:loading?"#312e81":"linear-gradient(135deg,#7c3aed,#3b82f6)", border:"none", padding:"14px", borderRadius:"8px", color:"#fff", fontWeight:700, cursor:loading?"not-allowed":"pointer", fontSize:"14px", letterSpacing:"0.05em"}}>
              {loading ? "GENERATING SCRIPT..." : "GENERATE SCRIPT ✨"}
            </button>
          </Card>
        </div>

        {/* RIGHT COLUMN: PREVIEW */}
        <div>
          <Card title="Live Preview" style={{height:"100%"}}>
             {!generated && !loading && (
               <div style={{height:"400px", display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column", color:"#4a4a6a", border:"2px dashed #1e1e3a", borderRadius:"12px"}}>
                 <div style={{fontSize:"40px", marginBottom:"10px"}}>🪄</div>
                 <div>Configure settings and click Generate</div>
               </div>
             )}

             {loading && (
               <div style={{height:"400px", display:"flex", alignItems:"center", justifyContent:"center", color:"#a78bfa", fontFamily:"'Space Mono',monospace"}}>
                 Writing script for {vertical}...
               </div>
             )}

             {generated && (
               <div>
                  {generated.error ? (
                    <div style={{color:"#ef4444", background:"#451a1a", padding:"15px", borderRadius:"8px", border:"1px solid #7f1d1d"}}>{generated.error}</div>
                  ) : (
                    <div style={{animation:"fadeIn 0.5s"}}>
                       <div style={{marginBottom:"20px", padding:"15px", background:"#1a1a2e", borderRadius:"8px", borderLeft:"3px solid #7c3aed"}}>
                          <div style={{fontSize:"10px", color:"#a78bfa", textTransform:"uppercase", fontWeight:700, marginBottom:"5px"}}>Selected Hook</div>
                          <div style={{fontSize:"14px", color:"#fff", fontStyle:"italic"}}>"{generated.hook}"</div>
                       </div>
                       
                       <div style={{whiteSpace:"pre-wrap", lineHeight:"1.6", fontSize:"14px", color:"#e2e8f0"}}>
                          <h3 style={{color:"#fff", fontSize:"16px", borderBottom:"1px solid #333", paddingBottom:"10px"}}>Script Content</h3>
                          {generated.script}
                       </div>

                       <div style={{marginTop:"30px", paddingTop:"20px", borderTop:"1px solid #1e1e3a", display:"grid", gridTemplateColumns:"1fr 1fr", gap:"20px"}}>
                          <div>
                            <div style={S.label}>Visual Direction</div>
                            <ul style={{paddingLeft:"20px", fontSize:"12px", color:"#94a3b8"}}>
                              {generated.shotList?.map((s,i)=><li key={i} style={{marginBottom:"5px"}}>{s}</li>)}
                            </ul>
                          </div>
                          <div>
                            <div style={S.label}>On-Screen Text</div>
                             <div style={{display:"flex", flexWrap:"wrap", gap:"5px"}}>
                               {generated.onScreenText?.map((t,i)=><Badge key={i} text={t} variant="default" />)}
                             </div>
                          </div>
                       </div>
                    </div>
                  )}
               </div>
             )}
          </Card>
        </div>
      </div>
    </div>
  );
}

// --- ROOT COMPONENT ---
export default function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthParamChange((currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  if (loading) {
     return <div style={{height:"100vh", display:"flex", justifyContent:"center", alignItems:"center", background:"#050510", color:"#6d6d9e", fontFamily:"sans-serif"}}>Loading...</div>;
  }

  return (
    <>
      {user ? <AuthenticatedApp user={user} /> : <AuthGate />}
    </>
  );
}